/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  LockClosedIcon, 
  EyeIcon, 
  ShareIcon, 
  ShieldCheckIcon,
  ChatBubbleBottomCenterTextIcon,
  InboxIcon
} from '@heroicons/react/24/outline';
import Footersection from '../views/Home/Footersection';
import Navbarsection from '../views/Home/Navbarsection';

export default function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState(0);

  const policySections = [
    {
      title: "Data Collection",
      icon: <EyeIcon className="w-6 h-6" />,
      content: "We collect personal information that you voluntarily provide to us when you register on the website, express an interest in obtaining information about us or our products and services, or otherwise when you contact us."
    },
    {
      title: "How We Use Data",
      icon: <ChatBubbleBottomCenterTextIcon className="w-6 h-6" />,
      content: "We use personal information collected via our website for a variety of business purposes described below. We process your personal information for these purposes in reliance on our legitimate business interests, in order to enter into or perform a contract with you."
    },
    {
      title: "Data Sharing",
      icon: <ShareIcon className="w-6 h-6" />,
      content: "We only share information with your consent, to comply with laws, to provide you with services, to protect your rights, or to fulfill business obligations. We do not sell your personal data to third parties."
    },
    {
      title: "Security Measures",
      icon: <LockClosedIcon className="w-6 h-6" />,
      content: "We have implemented appropriate technical and organizational security measures designed to protect the security of any personal information we process. However, please also remember that we cannot guarantee that the internet itself is 100% secure."
    },
    {
      title: "Your Rights",
      icon: <ShieldCheckIcon className="w-6 h-6" />,
      content: "You have the right to request access to the personal information we collect from you, change that information, or delete it in some circumstances. To request to review, update, or delete your personal information, please contact us."
    }
  ];

  return (
    <>
    <Navbarsection/>
    <div className="bg-[#fffcf9] min-h-screen py-16 px-4 md:px-8 selection:bg-orange-100">
      
      {/* BACKGROUND ORBS */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10">
        <div className="absolute top-[-5%] left-[-5%] w-[400px] h-[400px] bg-orange-100/40 rounded-full blur-[100px]" />
        <div className="absolute bottom-[-5%] right-[-5%] w-[300px] h-[300px] bg-orange-200/30 rounded-full blur-[80px]" />
      </div>

      <div className="max-w-6xl mx-auto">
        
        {/* HERO HEADER */}
        <header className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center text-center"
          >
            <div className="w-16 h-16 bg-orange-600 rounded-3xl flex items-center justify-center text-white shadow-2xl shadow-orange-200 mb-8 transform rotate-12">
               <ShieldCheckIcon className="w-10 h-10" />
            </div>
            <TypographyH1>Privacy Policy</TypographyH1>
            <p className="text-gray-400 font-bold uppercase tracking-[0.3em] text-xs mt-4">
              Your Trust is our Priority • v1.0 • 2026
            </p>
          </motion.div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* SIDE NAVIGATION (Desktop Only) */}
          <aside className="hidden lg:block lg:col-span-4 sticky top-10">
            <div className="bg-white/50 backdrop-blur-xl rounded-[2.5rem] p-4 border border-orange-50 shadow-sm">
              {policySections.map((section, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveSection(idx)}
                  className={`w-full flex items-center gap-4 p-5 rounded-2xl transition-all duration-300 mb-2 ${activeSection === idx ? 'bg-orange-600 text-white shadow-lg shadow-orange-100' : 'text-gray-500 hover:bg-orange-50 hover:text-orange-600'}`}
                >
                  {section.icon}
                  <span className="font-black uppercase text-xs tracking-widest">{section.title}</span>
                </button>
              ))}
            </div>
          </aside>

          {/* MAIN CONTENT AREA */}
          <main className="lg:col-span-8">
            <div className="space-y-10">
              {policySections.map((section, idx) => (
                <motion.section
                  key={idx}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className={`p-10 rounded-[3rem] transition-all border-2 ${activeSection === idx ? 'bg-white border-orange-500 shadow-2xl shadow-orange-100/50' : 'bg-white/40 border-transparent opacity-60'}`}
                >
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-6 ${activeSection === idx ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                    {section.icon}
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 uppercase tracking-tighter mb-4">
                    {section.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed font-medium text-lg">
                    {section.content}
                  </p>
                </motion.section>
              ))}
            </div>

            {/* CONTACT FOOTER CARD */}
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              className="mt-16 bg-black p-10 md:p-14 rounded-[3.5rem] text-white relative overflow-hidden"
            >
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-orange-600 rounded-full blur-[80px] opacity-30" />
              
              <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                <div className="text-center md:text-left">
                  <h4 className="text-3xl font-black mb-2 uppercase tracking-tight">Need Clarity?</h4>
                  <p className="text-gray-400 font-medium max-w-sm">
                    If you have questions about how we handle your data, our team is here to help.
                  </p>
                </div>
                <div className="flex flex-col gap-4 w-full md:w-auto">
                  <a href="mailto:privacy@mahakalsociety.in" className="bg-orange-600 hover:bg-orange-500 px-8 py-5 rounded-2xl font-black text-sm tracking-[0.2em] flex items-center justify-center gap-3 transition-all active:scale-95 shadow-xl shadow-orange-600/20">
                    <InboxIcon className="w-5 h-5" /> CONTACT US
                  </a>
                </div>
              </div>
            </motion.div>

            <div className="mt-10 text-center">
              <p className="text-[10px] font-black text-gray-300 uppercase tracking-[0.5em]">
                Last Revised: May 2026 • Secure & Transparent
              </p>
            </div>
          </main>

        </div>
      </div>
    </div>
    <Footersection/>
    </>
    
  );
}

/* --- HELPER COMPONENTS --- */
function TypographyH1({ children }) {
  return (
    <h1 className="text-6xl md:text-8xl font-black text-gray-900 leading-[0.9] tracking-tighter">
      {children}
    </h1>
  );
}